import { useEffect, useState } from 'react'
import axios from 'axios'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { BadgeEuro, ChevronDown, LockKeyhole, PackageOpen, Ruler, ShoppingBag, Sparkles, ZoomIn } from 'lucide-react'
import Modal from '../components/Modal'
import PageHeader from '../components/PageHeader'
import { useAuth } from '../hooks/useAuth'
import { useCart } from '../contexts/cartStore'
import { formatPrice, type ShopProduct } from '../types/Shop'

const Shop = () => {
  const { user } = useAuth()
  const { addItem, itemCount } = useCart()
  const [products, setProducts] = useState<ShopProduct[]>([])
  const [selectedVariants, setSelectedVariants] = useState<Record<number, number>>({})
  const [enlargedProduct, setEnlargedProduct] = useState<ShopProduct | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    setLoading(true)
    axios.get<ShopProduct[]>('/api/shop/products')
      .then(({ data }) => {
        if (!active) return
        setProducts(data)
        setError('')
      })
      .catch(() => active && setError('La boutique est temporairement indisponible.'))
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [user?.id])

  const chooseVariant = (product: ShopProduct) => {
    const selectedId = selectedVariants[product.id]
    return product.variants.find((variant) => variant.id === selectedId)
      ?? product.variants.find((variant) => variant.stock > 0)
      ?? product.variants[0]
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Équipement du dojo"
        title="La boutique du club"
        description="Goodies et équipements à retirer directement au club. Les adhérents connectés profitent d’une unité à tarif privilégié pour chaque produit."
        icon={<ShoppingBag />}
        actions={<Link to="/panier" className="btn-primary"><ShoppingBag className="h-4 w-4" /> Panier · {itemCount}</Link>}
      />

      <div className="shop-benefit-banner">
        <span aria-hidden="true"><BadgeEuro /></span>
        <div>
          <strong>{user ? 'Votre avantage adhérent est calculé automatiquement' : 'Un tarif est réservé aux adhérents'}</strong>
          <p>{user ? 'La première unité éligible de chaque produit passe au tarif adhérent dans le panier.' : 'Connectez-vous avant de commander pour en profiter.'}</p>
        </div>
        {!user && <Link to="/login" className="btn-secondary"><LockKeyhole className="h-4 w-4" /> Se connecter</Link>}
      </div>

      {loading && (
        <div className="shop-grid" aria-label="Chargement des produits">
          {[1, 2, 3].map((item) => <div className="shop-product-skeleton" key={item} />)}
        </div>
      )}

      {!loading && error && (
        <div className="empty-state"><PackageOpen className="mx-auto mb-4 h-10 w-10 text-red-600" /><h2>Impossible de charger la boutique</h2><p className="mt-2 text-gray-600">{error}</p></div>
      )}

      {!loading && !error && products.length === 0 && (
        <div className="empty-state"><Sparkles className="mx-auto mb-4 h-10 w-10 text-emerald-600" /><h2>La boutique se prépare</h2><p className="mt-2 text-gray-600">Les premiers produits seront bientôt disponibles.</p></div>
      )}

      {!loading && !error && products.length > 0 && (
        <section className="shop-grid" aria-label="Produits de la boutique">
          {products.map((product) => {
            const variant = chooseVariant(product)
            const outOfStock = !variant || variant.stock <= 0
            return (
              <article className="shop-product-card" key={product.id}>
                <div className="shop-product-image">
                  <button
                    type="button"
                    className="shop-product-image-button"
                    onClick={() => setEnlargedProduct(product)}
                    aria-label={`Agrandir l’image de ${product.name}`}
                  >
                    <img src={product.imageUrl} alt={product.name} />
                    <span className="shop-product-zoom" aria-hidden="true"><ZoomIn /></span>
                  </button>
                  {product.memberPriceEligible && <span className="shop-member-badge">Tarif adhérent disponible</span>}
                  {product.variants.every((item) => item.stock === 0) && <span className="shop-stock-badge">Rupture de stock</span>}
                </div>
                <div className="shop-product-body">
                  <div>
                    <p className="eyebrow !mb-2">Goodie du club</p>
                    <h2>{product.name}</h2>
                    <p className="shop-product-description">{product.description}</p>
                  </div>
                  <div className="shop-price-row">
                    <div><small>Prix public</small><strong>{formatPrice(product.publicPriceCents)}</strong></div>
                    <div className={product.memberPriceEligible ? 'shop-price-member-active' : ''}><small>Prix adhérent</small><strong>{formatPrice(product.memberPriceCents)}</strong></div>
                  </div>
                  <label className="shop-size-field" htmlFor={`shop-product-size-${product.id}`}>
                    <span className="shop-size-heading">
                      <span><Ruler aria-hidden="true" /> Taille</span>
                      <small className={outOfStock ? 'shop-size-stock-empty' : ''}>
                        {variant ? (outOfStock ? 'Épuisée' : `${variant.stock} en stock`) : 'Indisponible'}
                      </small>
                    </span>
                    <span className="shop-size-control">
                      <select
                        id={`shop-product-size-${product.id}`}
                        className="input-field"
                        value={variant?.id ?? ''}
                        onChange={(event) => setSelectedVariants((current) => ({ ...current, [product.id]: Number(event.target.value) }))}
                      >
                        {product.variants.map((item) => <option value={item.id} key={item.id} disabled={item.stock === 0}>{item.label} — {item.stock > 0 ? `${item.stock} disponible${item.stock > 1 ? 's' : ''}` : 'épuisé'}</option>)}
                      </select>
                      <ChevronDown aria-hidden="true" />
                    </span>
                  </label>
                  <button
                    type="button"
                    className="btn-primary w-full justify-center"
                    disabled={outOfStock}
                    onClick={() => {
                      if (!variant) return
                      addItem(variant.id)
                      toast.success(`${product.name} ajouté au panier`)
                    }}
                  >
                    <ShoppingBag className="h-4 w-4" /> {outOfStock ? 'Indisponible' : 'Ajouter au panier'}
                  </button>
                </div>
              </article>
            )
          })}
        </section>
      )}

      <Modal
        isOpen={enlargedProduct !== null}
        onClose={() => setEnlargedProduct(null)}
        title={enlargedProduct?.name ?? 'Photo du produit'}
        eyebrow="Aperçu du produit"
        size="lg"
      >
        {enlargedProduct && (
          <div className="shop-product-image-preview">
            <img src={enlargedProduct.imageUrl} alt={enlargedProduct.name} />
          </div>
        )}
      </Modal>
    </div>
  )
}

export default Shop
